// A calm one-line banner when a new brief is ready: the one thing to look at first, then which
// brief it is ("Needs you: Sign the lease. · Your 1pm brief"). It links to the brief and can
// be waved away; it never flashes or counts up. On the feed it says only which brief it is and
// "Read it": the feed's own "Needs you" list already names the one thing.
import { Link } from "@tanstack/react-router";
import { Sunrise, X } from "lucide-react";
import { useBriefs } from "@web/lib/notifyQueries";
import { useNow } from "@web/lib/useNow";
import { notifyStore, useDismissedBriefs } from "./notifyStore";
import { bannerBrief, bannerMode } from "./wording";

export function BriefBanner({ pathname, enabled }: { pathname: string; enabled: boolean }) {
  const briefs = useBriefs(enabled);
  const now = useNow(60_000);
  const dismissed = useDismissedBriefs();
  const brief = bannerBrief(briefs.data, now, dismissed);
  const mode = bannerMode(pathname);
  if (!brief || mode === "hidden") return null;
  return (
    <div
      role="status"
      aria-label="New brief"
      className="flex shrink-0 items-center gap-3 border-b border-line bg-paper px-4 py-2 text-sm"
    >
      <Sunrise aria-hidden className="size-4 shrink-0 text-accent-text" />
      {mode === "short" ? (
        <Link
          to="/brief/$briefId"
          params={{ briefId: brief.id }}
          className="min-w-0 flex-1 truncate text-ink"
        >
          {brief.label}
          <span className="text-muted"> · </span>
          <span className="text-accent-text underline underline-offset-2">Read it</span>
        </Link>
      ) : (
        <>
          <Link
            to="/brief/$briefId"
            params={{ briefId: brief.id }}
            className="min-w-0 flex-1 truncate text-ink hover:underline"
          >
            <span className="font-medium">{brief.headline}</span>
            <span className="text-muted"> · {brief.label}</span>
          </Link>
          <Link
            to="/brief/$briefId"
            params={{ briefId: brief.id }}
            className="hidden shrink-0 text-accent-text underline underline-offset-2 sm:inline"
          >
            Read it
          </Link>
        </>
      )}
      <button
        type="button"
        onClick={() => notifyStore.dismissBrief(brief.id)}
        aria-label="Not now"
        title="Not now"
        className="shrink-0 rounded-md p-1 text-muted hover:bg-sunk hover:text-ink"
      >
        <X aria-hidden className="size-4" />
      </button>
    </div>
  );
}
